/**
 * Google Ads competitive analysis API client (P5.1c).
 *
 * Backend routes:
 *   GET  /google-ads/competitive/auction-insights?tenant_id=&limit=
 *   GET  /google-ads/competitive/auction-insights/top?tenant_id=&period_start=&period_end=&limit=
 *   POST /google-ads/competitive/import?tenant_id=&campaign_id=
 *   POST /google-ads/competitive/trigger?tenant_id=&period_start=&period_end=
 *
 * Master flag: COMPETITIVE_ANALYSIS_ENABLED
 */
import { apiClient } from './apiClient';

export interface AuctionInsightRow {
  id: string;
  tenant_id: string;
  customer_id: string;
  campaign_id: string | null;
  period_start: string;
  period_end: string;
  competitor_domain: string;
  impression_share: number | null;
  overlap_rate: number | null;
  position_above_rate: number | null;
  top_of_page_rate: number | null;
  abs_top_of_page_rate: number | null;
  outranking_share: number | null;
  created_at: string;
}

export interface CompetitiveTriggerResult {
  tenant_id: string;
  customer_id: string;
  period_start: string;
  period_end: string;
  campaign_id: string | null;
  rows_upserted: number;
  competitor_count: number;
  skipped_reason: string | null;
}

export interface CompetitiveImportResult {
  tenant_id: string;
  customer_id: string;
  period_start: string;
  period_end: string;
  campaign_id: string | null;
  file_name: string | null;
  rows_upserted: number;
  competitor_count: number;
  skipped_reason: string | null;
  warnings: string[];
}

export async function listAuctionInsights(
  tenantId: string,
  limit = 100,
  maxAgeDays?: number,
): Promise<AuctionInsightRow[]> {
  const { data } = await apiClient.get<AuctionInsightRow[]>(
    '/google-ads/competitive/auction-insights',
    {
      params: {
        tenant_id: tenantId,
        limit,
        max_age_days: maxAgeDays,
      },
    },
  );
  return data;
}

export async function listTopCompetitors(params: {
  tenantId: string;
  periodStart: string;
  periodEnd: string;
  limit?: number;
}): Promise<AuctionInsightRow[]> {
  const { data } = await apiClient.get<AuctionInsightRow[]>(
    '/google-ads/competitive/auction-insights/top',
    {
      params: {
        tenant_id: params.tenantId,
        period_start: params.periodStart,
        period_end: params.periodEnd,
        limit: params.limit ?? 10,
      },
    },
  );
  return data;
}

export async function triggerCompetitivePull(params: {
  tenantId: string;
  campaignId?: string;
  periodStart?: string;
  periodEnd?: string;
}): Promise<CompetitiveTriggerResult> {
  const { data } = await apiClient.post<CompetitiveTriggerResult>(
    '/google-ads/competitive/trigger',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        campaign_id: params.campaignId,
        period_start: params.periodStart,
        period_end: params.periodEnd,
      },
    },
  );
  return data;
}

export async function importCompetitiveAuctionInsights(params: {
  tenantId: string;
  file: File;
  campaignId?: string;
  periodStart?: string;
  periodEnd?: string;
}): Promise<CompetitiveImportResult> {
  const formData = new FormData();
  formData.append('file', params.file);

  const { data } = await apiClient.post<CompetitiveImportResult>(
    '/google-ads/competitive/import',
    formData,
    {
      params: {
        tenant_id: params.tenantId,
        campaign_id: params.campaignId,
        period_start: params.periodStart,
        period_end: params.periodEnd,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return data;
}
